import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog-sticky-footer';
import { Check, Eye, AlertTriangle, PersonStanding, ExternalLink, ArrowLeft } from 'lucide-react';
import { DoOverDialog } from '@components/Dialog/do-over';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@lib/utils';
import { HairIcon } from '@components/Icons/hair';
import { FacialHairIcon } from '@components/Icons/facial-hair';
import { Glasses } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Checkbox } from '@components/ui/checkbox';
import { api } from '@/trpc/react';
import { IMPROVE_RESULTS_DIALOG_ID } from '@/atoms/improveResultsDialog';
import { useModelDoOver } from '@hooks/useModelDoOver';

interface ImproveResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelId: string;
}

const options = [
  {
    id: 'resemblance',
    label: 'Overall resemblance',
    description: "Doesn't capture my likeness",
    icon: AlertTriangle
  },
  {
    id: 'gender',
    label: 'Gender',
    description: 'Incorrect gender appearance shown',
    icon: PersonStanding
  },
  {
    id: 'skin-tone',
    label: 'Skin tone',
    description: 'Skin color needs correcting',
    icon: PersonStanding
  },
  {
    id: 'age',
    label: 'Age',
    description: 'Too young or too old',
    icon: PersonStanding
  },
  {
    id: 'hair',
    label: 'Hair',
    description: 'Hair style, length, or color is wrong',
    icon: HairIcon
  },
  {
    id: 'eye-color',
    label: 'Eye color',
    description: 'Wrong eye color shown',
    icon: Eye
  },
  {
    id: 'facial-hair',
    label: 'Facial hair',
    description: 'Facial hair incorrectly shown',
    icon: FacialHairIcon
  },
  {
    id: 'glasses',
    label: 'Eyewear',
    description: 'Glasses missing or incorrectly added',
    icon: Glasses
  }
];

interface DontShowAgainProps {
  onCheckedChange: (checked: boolean) => void;
  checked: boolean;
}

const DontShowAgain = ({ onCheckedChange, checked }: DontShowAgainProps) => {
  return (
    <div className="flex items-center gap-2 mx-auto w-fit">
      <Checkbox id="photo-tips" checked={checked} onCheckedChange={checked => onCheckedChange(checked as boolean)} />
      <label
        htmlFor="photo-tips"
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        Don't show again
      </label>
    </div>
  );
};

export function ImproveResultsDialog({ open, onOpenChange, modelId }: ImproveResultsDialogProps) {
  const [selectedOption, setSelectedOption] = React.useState<string>();
  const [currentView, setCurrentView] = React.useState<'selection' | 'try-do-over' | 'edit-model-details'>('selection');
  const [isChecked, setIsChecked] = React.useState(false);
  const router = useRouter();
  const model = api.model.get.useQuery(
    {
      id: modelId!
    },
    {
      enabled: !!modelId
    }
  );
  const { handleDoOver, hasDoOverCredits, userCreditsQuery } = useModelDoOver({});
  const utils = api.useUtils();

  const dismissMutation = api.userDismissedItem.dismissItem.useMutation({
    onSuccess: () => {
      utils.userDismissedItem.hasDismissedItem.invalidate({
        itemId: IMPROVE_RESULTS_DIALOG_ID,
        itemType: 'dialog'
      });
    }
  });

  const handleDismiss = () => {
    if (isChecked) {
      dismissMutation.mutate({
        itemId: IMPROVE_RESULTS_DIALOG_ID,
        itemType: 'dialog'
      });
    }
  };

  const handleContinue = () => {
    if (!selectedOption) return;

    if (selectedOption === 'resemblance') {
      setCurrentView('try-do-over');
    } else {
      setCurrentView('edit-model-details');
    }
  };

  const handleBack = () => {
    setCurrentView('selection');
  };

  const handlePushToDoOver = () => {
    onOpenChange(false);
    const searchParams = new URLSearchParams({ modelId });
    router.push(`/do-over?${searchParams.toString()}`);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'try-do-over':
        return (
          <div className="p-6">
            <div className="absolute top-6 left-6">
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-shrink-0 ">
              <Image
                src="https://storage.prompt-hunt.workers.dev/cm742zrf8000lhiod8aok3qo1_4"
                alt="Do-Over Card"
                width={1024}
                height={1024}
                className="w-32 h-32 rounded-lg mx-auto mb-6"
              />
            </div>
            <DialogHeader className="flex flex-col gap-2 space-y-0 text-left">
              <DialogTitle className="text-xl font-bold text-center">Let's improve your results!</DialogTitle>
              <DialogDescription className="text-center text-base text-foreground">
                If your model isn't looking like you, you have the option to retrain your model with a fresh set of
                photos.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6">
              <DontShowAgain checked={isChecked} onCheckedChange={setIsChecked} />
            </div>
          </div>
        );

      case 'edit-model-details':
        return (
          <div className="p-6">
            <div className="absolute top-6 left-6">
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-shrink-0 ">
              <Image
                src="https://storage.prompt-hunt.workers.dev/cm8nx8xh7000vjz9we344kz89_4"
                alt="Do-Over Card"
                width={1024}
                height={1024}
                className="w-32 h-32 rounded-lg mx-auto mb-6"
              />
            </div>
            <DialogHeader className="flex flex-col gap-2 space-y-0 text-left">
              <DialogTitle className="text-xl font-bold text-center">Got it - let's update your model!</DialogTitle>
              <DialogDescription className="text-center text-base text-foreground">
                If you need to update the {selectedOption} for your model, you can do that free of charge by editing the
                model details.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6">
              <DontShowAgain checked={isChecked} onCheckedChange={setIsChecked} />
            </div>
          </div>
        );

      default:
        return (
          <div className="p-6">
            <div className="-mx-6 -mt-6 mb-6">
              <Image
                src="https://storage.prompt-hunt.workers.dev/cm8ntfrfr0018phxfnteiaekm_1"
                alt="What's wrong with this image?"
                width={1024}
                height={768}
                className="w-full h-auto object-cover aspect-video overflow-hidden"
              />
            </div>
            <DialogHeader className="flex flex-col gap-2 space-y-0 text-left">
              <DialogTitle className="text-xl font-bold">Let's see if we can improve your results</DialogTitle>
              <DialogDescription className="text-sm text-foreground mb-6">
                Not every photo will be perfect, but we might be able to help you improve your overall results.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6">
              <p className="font-semibold mb-2">What's off about your photo?</p>
              <RadioGroup value={selectedOption} onValueChange={setSelectedOption} className="space-y-1">
                {options.map(option => (
                  <div
                    key={option.id}
                    className={`relative flex cursor-pointer items-center justify-between rounded-lg border bg-accent/10 px-4 py-2 transition-all ${
                      selectedOption === option.id ? 'border-accent' : 'border-border bg-background hover:bg-secondary'
                    }`}
                    onClick={() => setSelectedOption(option.id)}
                  >
                    <RadioGroupItem value={option.id} id={option.id} className="peer sr-only" />
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex items-center self-center">
                        {React.createElement(option.icon, { size: 20 })}
                      </div>
                      <div className="flex-1">
                        <Label htmlFor={option.id} className="flex w-full items-center text-sm font-medium">
                          {option.label}
                        </Label>
                        <p className="mt-0 text-sm text-muted-foreground">{option.description}</p>
                      </div>
                    </div>
                    {selectedOption === option.id && (
                      <div
                        className={cn(
                          'ml-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground',
                          selectedOption === option.id && 'bg-accent'
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex flex-col gap-0 p-0 h-[100dvh] md:h-auto md:max-h-[85vh] sm:max-w-lg [&>button:last-child]:hidden">
          <div className="flex-1 overflow-y-auto">{renderContent()}</div>
          <DialogFooter className="flex-shrink-0 border-t border-border px-6 py-4 sm:grid sm:grid-cols-2 sm:gap-4">
            {currentView === 'selection' ? (
              <>
                <DialogClose asChild>
                  <Button variant="secondary" className="w-full">
                    Cancel
                  </Button>
                </DialogClose>
                <Button onClick={handleContinue} disabled={!selectedOption} className="w-full" variant="default">
                  Continue
                </Button>
              </>
            ) : currentView === 'try-do-over' ? (
              <>
                <DialogClose asChild>
                  <Button variant="secondary" className="w-full" onClick={handleDismiss}>
                    Done
                  </Button>
                </DialogClose>
                <Button variant="accent" onClick={handlePushToDoOver} className="w-full">
                  Try Do-Over
                </Button>
              </>
            ) : (
              <>
                <DialogClose asChild>
                  <Button variant="secondary" className="w-full" onClick={handleDismiss}>
                    Done
                  </Button>
                </DialogClose>
                <Button variant="accent" asChild className="w-full">
                  <Link
                    href={`/dashboard/${modelId}?tab=modelDetails`}
                    target="_blank"
                    className="flex items-center gap-2"
                  >
                    Edit details
                    <ExternalLink size={14} />
                  </Link>
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {open && (
        <DoOverDialog
          isPending={model.isPending}
          modelId={modelId}
          onConfirm={() => handleDoOver(model.data!, model.data!.modelRepository?.name!)}
          hasDoOverCredits={Boolean(hasDoOverCredits)}
          doOverCreditsCount={userCreditsQuery.data?.redoCredits}
          onOpenChange={onOpenChange}
        />
      )}
    </>
  );
}
